import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import DatePicker from "react-datepicker";
import get from "lodash/get";
import { crossIcon, plusLightIcon } from "../../../../assets/icons";
import { selectSelectedModal } from "../../Modal";
import { actions } from "../../Modal/modal.reducers";
import {
  FILTER_OPERATORS,
  BOOLEAN_VALUES,
} from "../../AppModals/modals.constants";
import {
  selectSelectedDataTable,
  selectTableDataFilters,
} from "../../../../pages/DataModels";
import { actions as dataModelActions } from "../../../../pages/DataModels/dataModels.reducers";
import { INTERFACES } from "../../AppDrawers/drawers.constants";
import { INDEX, EMPTY_STRING, REGEX } from "../../../../constants";
import { getDateFromTimestamp } from "../../../../helpers";

const { setSelectedModal } = actions;
const { setFormattedFilters, setTableDataFilters } = dataModelActions;
const {
  DATE_TIME,
  BOOLEAN,
  INPUT,
  TEXT_AREA,
  REPEATER_FIELDS,
  FLOAT_NUMBER,
  SELECT,
  FILE,
  IMAGE,
  NUMBER,
  EMAIL,
} = INTERFACES;
const { FIRST } = INDEX;
const { STRING_OPERATORS, DEFAULT, ARITHMETIC_OPERATORS, BOOLEAN_OPERATORS } =
  FILTER_OPERATORS;
const { WHITESPACES_AT_START } = REGEX;
const COLUMN = "column";
const STARTS_WITH = "startsWith";
const ENDS_WITH = "endsWith";
const LIKE = "like";
const ILIKE = " ILIKE ";
const BETWEEN = "BETWEEN";
const NOT_BETWEEN = "NOT BETWEEN";
const EQUAL = "=";
const NOT_EQUAL = "!=";

const FilterDataModal = () => {
  const dispatch = useDispatch();
  const selectedModal = useSelector(selectSelectedModal);
  const selectedTable = useSelector(selectSelectedDataTable);
  const tableDataFilters = useSelector(selectTableDataFilters);

  const [columns, setColumns] = useState([]);
  const [shouldShowCloseButton, setShouldShowCloseButton] = useState(true);
  const [filters, setFilters] = useState([]);
  const [columnsInterfaces, setColumnsInterfaces] = useState();
  const [columnsDefaultValues, setColumnsDefaultValues] = useState();
  const [defaultFilter, setDefaultFilter] = useState();

  const isWhiteSpace = (value) => !WHITESPACES_AT_START.test(value);
  const isButtonDisabled = (values) =>
    values.some(
      (key) =>
        key.value === EMPTY_STRING || isWhiteSpace(key.value) || !key.operator
    );

  const isResetButtonDisabled = (values) =>
    !values.some(
      (key) => key.operator || key.value
    );

  useEffect(() => {
    if (selectedTable) {
      const columns = selectedTable.schema.properties.filter(
        (column) => !column.meta.isKey
      );
      setColumns(columns);
    }
  }, [selectedTable]);

  useEffect(() => {
    if (columns.length) {
      const defaultFilter = {
        column: columns[FIRST].schema.key,
        operator: EMPTY_STRING,
        value:
          columns[FIRST].meta.interface === DATE_TIME ||
          columns[FIRST].meta.interface === BOOLEAN
            ? columns[FIRST].schema.defaultValue
            : EMPTY_STRING,
      };
      setDefaultFilter(defaultFilter);

      const columnsDefaultValues = columns.reduce(
        (acc, each) => ({
          ...acc,
          [each.schema.key]: each.schema.defaultValue,
        }),
        {}
      );
      setColumnsDefaultValues(columnsDefaultValues);

      const columnsInterfaces = columns.reduce(
        (acc, each) => ({
          ...acc,
          [each.schema.key]: each.meta.interface,
        }),
        {}
      );
      setColumnsInterfaces(columnsInterfaces);
    }
  }, [columns]);

  useEffect(() => {
    tableDataFilters.length && setFilters(tableDataFilters);
  }, [tableDataFilters]);

  useEffect(() => {
    if (!filters.length && defaultFilter)
      setFilters([{ id: uuid(), ...defaultFilter }]);
  }, [filters, defaultFilter]);

  useEffect(() => setShouldShowCloseButton(selectedModal), [selectedModal]);

  const parseDate = (dateString) => {
    const parsedDate = new Date(dateString);
    return !isNaN(parsedDate) ? parsedDate : null;
  }

  const handleOnChange = useCallback(
    (e, data, date) => {
      const updatedFilters = structuredClone(filters).map((filterValue) => {
        if (filterValue.id === data.id && date) {
          filterValue.value = new Date(date).toISOString();
        } else if (filterValue.id === data.id) {
          filterValue[e.target.name] = e.target.value;
          if (e.target.name === COLUMN && e.target.value !== filterValue.column)
            filterValue.value =
              columnsInterfaces[e.target.value] === DATE_TIME ||
                columnsInterfaces[e.target.value] === BOOLEAN
                ? columnsDefaultValues[e.target.value]
                : EMPTY_STRING;
        }
        return filterValue;
      });
      setFilters(updatedFilters);
    },
    [filters, columnsInterfaces, columnsDefaultValues]
  );

  const handleCloseModal = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        setShouldShowCloseButton(false);
        dispatch(setSelectedModal(null));
      }
    },
    [dispatch]
  );

  const handleAddField = useCallback(() => {
    setFilters([...filters, { id: uuid(), ...defaultFilter }]);
  }, [filters, defaultFilter]);

  const handleRemoveFilter = useCallback(
    (data) => {
      if (filters.length > 1) {
        const updatedFilters = filters.filter(
          (formValue) => formValue.id !== data.id
        );
        setFilters(updatedFilters);
      } else if (filters.length === 1) handleCloseModal({});
    },
    [filters, handleCloseModal]
  );

  const getQueryForDateTimeEquality = useCallback((currentDate, operator) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + 1);
    const succeedingDate = date.toISOString();
    const startDate = getDateFromTimestamp(currentDate);
    const endDate = getDateFromTimestamp(succeedingDate);
    const operators = operator === NOT_EQUAL ? NOT_BETWEEN : BETWEEN;
    return ` ${operators} '${startDate}' AND '${endDate}'`;
  }, []);

  const getFilterValue = useCallback(
    (filter) => {
      const { column, operator, value } = filter;
      switch (operator) {
        case LIKE:
          return ILIKE + `'%25${value}%25'`;
        case STARTS_WITH:
          return ILIKE + `'${value}%25'`;
        case ENDS_WITH:
          return ILIKE + `'%25${value}'`;
        case EQUAL:
        case NOT_EQUAL:
          if (get(columnsInterfaces, column, EMPTY_STRING) === DATE_TIME)
            return getQueryForDateTimeEquality(value, operator);
          else return operator + `'${value}'`;
        default:
          return operator + `'${value}'`;
      }
    },
    [getQueryForDateTimeEquality, columnsInterfaces]
  );

  const handleOnSubmit = useCallback(
    (values) => {
      dispatch(setTableDataFilters(values));
      const updatedFilters = values.reduce(
        (obj, filter, count) => ({
          ...obj,
          [`filter${count + 1}`]: `"${filter.column}"` + getFilterValue(filter),
        }),
        {}
      );
      dispatch(setFormattedFilters(updatedFilters));
      handleCloseModal({});
    },
    [dispatch, handleCloseModal, getFilterValue]
  );

  const getOperatorsList = useCallback(
    (value) => {
      const selectedColumn = columns.reduce(
        (selectedColumn, column) =>
          column.schema.key === value ? column : selectedColumn,
        {}
      );
      const columnInterface = selectedColumn.meta.interface;
      switch (columnInterface) {
        case INPUT:
        case TEXT_AREA:
        case NUMBER:
        case EMAIL:
          return STRING_OPERATORS;
        case FLOAT_NUMBER:
        case DATE_TIME:
          return ARITHMETIC_OPERATORS;
        case REPEATER_FIELDS:
        case BOOLEAN:
          return BOOLEAN_OPERATORS;
        default:
          return DEFAULT;
      }
    },
    [columns]
  );

  const getField = useCallback(
    (filter) => {
      const columnInterface = columnsInterfaces[filter.column];

      switch (columnInterface) {
        case DATE_TIME:
          return (
            <DatePicker
              selected={parseDate(filter.value)}
              value={new Date(filter.value)}
              dateFormat="dd/MM/yyyy"
              className="form-control"
              name="value"
              onChange={(date) => handleOnChange(EMPTY_STRING, filter, date)}
            />
          );
        case BOOLEAN:
          return (
            <Field
              as="select"
              name="value"
              className="form-control"
              onChange={(e) => {
                handleOnChange(e, filter);
              }}
              value={filter.value}
            >
              {BOOLEAN_VALUES.map((each) => (
                <option key={each.name} value={each.value}>
                  {each.name}
                </option>
              ))}
            </Field>
          );
        case REPEATER_FIELDS:
          const repeaterFieldsOptions = columns.find(
            (column) => column.schema.key === filter.column
          )?.meta?.options;
          return (
            <Field
              as="select"
              name="value"
              className="form-control"
              onChange={(e) => {
                handleOnChange(e, filter);
              }}
              value={filter.value}
            >
              <option key="value" value={EMPTY_STRING}>
                Select an option
              </option>
              {repeaterFieldsOptions.map((each) => (
                <option key={each.value} value={each.value}>
                  {each.value}
                </option>
              ))}
            </Field>
          );
        default:
          return (
            <Field
              type="text"
              name="value"
              className="form-control"
              placeholder="Value"
              defaultValue={filter.value}
              onChange={(e) => handleOnChange(e, filter)}
            />
          );
      }
    },
    [columnsInterfaces, handleOnChange, columns]
  );

  return (
    <div
      className={classNames(
        "px-4 flex items-center fixed inset-0 z-[999] overflow-auto w-full transition-opacity",
        {
          " w-0 h-0 opacity-0": !shouldShowCloseButton,
        }
      )}
      onClick={handleCloseModal}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-md mx-auto my-6 ">
        <Formik
          enableReinitialize
          initialValues={filters}
          onSubmit={handleOnSubmit}
        >
          {({ values }) => {
            const footer = (
              <div className="flex p-4 pt-0">
                <button
                  className="btn xs:w-auto flex items-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  disabled={isButtonDisabled(values)}
                  onClick={handleAddField}
                >
                  <div className="icon mr-2">
                    <img src={plusLightIcon} width="15" alt="plusLightIcon" />
                  </div>
                  Add
                </button>
                <div className="ml-auto">
                  <button
                    className="btn btn-primary-light mr-2"
                    type="button"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary-light mr-2"
                    onClick={() => {
                      dispatch(setTableDataFilters([]));
                      dispatch(setFormattedFilters());
                      setFilters([defaultFilter]);
                    }}
                    disabled={isResetButtonDisabled(values)}
                  >
                    Reset
                  </button>
                  <button
                    className="btn btn-primary ml-1 text-white"
                    type="submit"
                    disabled={isButtonDisabled(values)}
                  >
                    Filter
                  </button>
                </div>
              </div>
            );
            return (
              <Form
              autoComplete="off"
              >
                <div className="p-4 pb-2 form-group">
                  {values.map((filter) => (
                    <div
                      key={filter.id}
                      className="flex flex-row items-center mb-2"
                    >
                      <div className="mr-4">
                        <label className="text-white">Column</label>
                        <Field
                          as="select"
                          name="column"
                          className="form-control"
                          onChange={(e) => {
                            handleOnChange(e, filter);
                          }}
                          value={filter.column}
                        >
                          {columns.map(
                            (column) =>
                              ![SELECT, FILE, IMAGE].includes(
                                column.meta.interface
                              ) && (
                                <option
                                  key={column.schema.key}
                                  value={column.schema.key}
                                >
                                  {column.name}
                                </option>
                              )
                          )}
                        </Field>
                      </div>
                      <div className="mr-4">
                        <label className="text-white">Operator</label>
                        <Field
                          as="select"
                          name="operator"
                          className="form-control"
                          onChange={(e) => {
                            handleOnChange(e, filter);
                          }}
                          value={filter.operator}
                        >
                          <option hidden>Choose Operator</option>
                          {getOperatorsList(filter.column).map((operator) => (
                            <option key={operator.name} value={operator.value}>
                              {operator.name}
                            </option>
                          ))}
                        </Field>
                      </div>
                      <div className="mr-4">
                        <label className="text-white">Value</label>
                        <div>
                        {getField(filter)}
                        </div>
                      </div>
                      <div className="w-6 h-6 shrink-0 mx-2">
                        <button
                          className="hover:opacity-80 active:opacity-100 pt-[50%]"
                          type="button"
                          onClick={() => handleRemoveFilter(filter)}
                        >
                          <img src={crossIcon} alt="delete" className="w-6" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {footer}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
export default FilterDataModal;
